import React, { useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Printer, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Componente de uma única etiqueta - Preview
const Etiqueta = ({ ciclo, showBorder = true }) => {
  const qrUrl = `${API_URL}/consulta/${ciclo.id}`;
  
  const dataCiclo = new Date(ciclo.data_hora || ciclo.created_at);
  const diasValidade = ciclo.dias_validade || 7;
  const dataValidade = new Date(dataCiclo);
  dataValidade.setDate(dataValidade.getDate() + diasValidade);
  
  const numeroCiclo = ciclo.numero_ciclo?.replace('CIC-', '') || ciclo.id?.slice(-8) || '';
  const metodo = ciclo.metodo ? ciclo.metodo.charAt(0).toUpperCase() + ciclo.metodo.slice(1) : 'Vapor';
  
  return (
    <div 
      className={`etiqueta ${showBorder ? 'border border-slate-400' : ''}`}
      style={{
        width: '47mm',
        height: '28mm',
        padding: '1.5mm',
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'row',
        gap: '2mm',
        fontFamily: 'Arial, sans-serif',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <div style={{ 
        width: '22mm', 
        minWidth: '22mm',
        height: '25mm', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
      }}>
        <QRCodeSVG 
          value={qrUrl}
          size={80}
          level="L"
          includeMargin={false}
        />
      </div>
      
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'space-between',
        overflow: 'hidden',
        fontSize: '7pt',
        lineHeight: '1.15',
        minWidth: 0,
      }}>
        <div style={{ 
          fontWeight: 'bold', 
          fontSize: '8pt', 
          borderBottom: '0.5pt solid #000',
          paddingBottom: '0.5mm',
          marginBottom: '0.5mm',
        }}>
          {numeroCiclo}
        </div>
        
        <div style={{ fontSize: '6.5pt', flex: 1 }}>
          <div style={{ marginBottom: '0.5mm' }}>
            <strong>Data:</strong> {dataCiclo.toLocaleDateString('pt-BR')}
          </div>
          <div style={{ marginBottom: '0.5mm' }}>
            <strong>Hora:</strong> {dataCiclo.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </div>
          <div style={{ marginBottom: '0.5mm' }}>
            <strong>Método:</strong> {metodo}
          </div>
          <div>
            <strong>{ciclo.autoclave_nome || 'Autoclave'}</strong>
          </div>
        </div>
        
        <div style={{ 
          backgroundColor: '#000', 
          color: '#fff',
          padding: '1mm',
          textAlign: 'center',
          marginTop: '0.5mm',
        }}>
          <div style={{ fontSize: '6pt', fontWeight: 'bold' }}>VÁLIDO ATÉ</div>
          <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
            {dataValidade.toLocaleDateString('pt-BR')}
          </div>
        </div>
      </div>
    </div>
  );
};

// Preview com duas etiquetas
const EtiquetaDupla = ({ ciclo }) => {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'row',
      gap: '3mm',
      width: '100mm',
      justifyContent: 'center',
    }}>
      <Etiqueta ciclo={ciclo} showBorder={true} />
      <Etiqueta ciclo={ciclo} showBorder={true} />
    </div>
  );
};

// Dialog Principal
export function EtiquetaPreviewDialog({ 
  open, 
  onOpenChange, 
  ciclo, 
  quantidade = 1, 
  onQuantidadeChange,
  diasValidade = 7,
  onDiasValidadeChange 
}) {
  const [loading, setLoading] = useState(false);
  
  if (!ciclo) return null;
  
  const cicloComValidade = { ...ciclo, dias_validade: diasValidade };
  
  // Abrir PDF para impressão direta
  const handlePrintPDF = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Abre o PDF em nova aba - usuário pode imprimir direto com Ctrl+P
      const url = `${API_URL}/api/etiquetas/ciclo/${ciclo.id}/pdf-etiqueta?quantidade=${quantidade}&dias_validade=${diasValidade}`;
      
      // Fazer fetch com autenticação
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Erro ao gerar PDF');
      }
      
      // Criar blob e abrir
      const blob = await response.blob();
      const pdfUrl = window.URL.createObjectURL(blob);
      
      // Abrir em nova aba
      const newWindow = window.open(pdfUrl, '_blank');
      
      if (newWindow) {
        toast.success('PDF aberto! Use Ctrl+P para imprimir.');
      } else {
        // Fallback: download
        const a = document.createElement('a');
        a.href = pdfUrl;
        a.download = `etiquetas_${ciclo.numero_ciclo}.pdf`;
        a.click();
        toast.info('PDF baixado. Abra e imprima.');
      }
      
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao gerar PDF');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="w-5 h-5" />
            Imprimir Etiquetas - {ciclo.numero_ciclo}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Configurações */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="quantidade">Quantidade</Label>
              <Input
                id="quantidade"
                type="number"
                min={1}
                max={50}
                value={quantidade}
                onChange={(e) => onQuantidadeChange?.(parseInt(e.target.value) || 1)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="validade">Dias de Validade</Label>
              <Input
                id="validade"
                type="number"
                min={1}
                max={365}
                value={diasValidade}
                onChange={(e) => onDiasValidadeChange?.(parseInt(e.target.value) || 7)}
                className="mt-1"
              />
            </div>
          </div>
          
          {/* Preview */}
          <div className="border rounded-lg p-3 bg-slate-50">
            <p className="text-xs text-slate-500 mb-2">Preview (50x30mm):</p>
            <div className="flex justify-center overflow-auto scale-90">
              <EtiquetaDupla ciclo={cicloComValidade} />
            </div>
          </div>
          
          {/* Botão Principal */}
          <Button 
            onClick={handlePrintPDF} 
            disabled={loading}
            className="w-full bg-green-600 hover:bg-green-700 h-12 text-lg"
            size="lg"
          >
            <Printer className="w-5 h-5 mr-2" />
            {loading ? 'Gerando...' : `Imprimir ${quantidade} Etiqueta${quantidade > 1 ? 's' : ''}`}
          </Button>
          
          {/* Instruções simples */}
          <div className="text-xs text-slate-500 text-center bg-blue-50 p-2 rounded">
            <strong>Após clicar:</strong> O PDF abrirá em nova aba → Pressione <kbd className="bg-slate-200 px-1 rounded">Ctrl</kbd> + <kbd className="bg-slate-200 px-1 rounded">P</kbd> → Selecione <strong>Argox</strong> → Imprimir
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="w-4 h-4 mr-2" />
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EtiquetaPreviewDialog;
